import { getItemUniqueKey, getPackageId } from './cart'
import { isBNBLLEHotelItem, isInstantBookingLEHotelItem } from 'lib/checkout/checkoutUtils'
import { bnblUpfrontExtraGuestSurchargeCharge } from 'lib/checkout/bnblExtraGuestSurcharge'
import moment from 'moment'
import { extractPkgInclusions } from 'lib/offer/inclusionUtils'
import { uniqueBy } from 'lib/array/arrayUtils'
import getLoyaltyProductType from 'loyalty/lib/getLoyaltyProductType'
import getHotelEncryptedMargin from 'loyalty/lib/margin/getHotelEncryptedMargin'
import getLEPackageItemTotals from './getLEPackageItemTotals'
import { findUnifiedRateForCheckoutItem, getOptInBundleInclusionsFromRate, isUnifiedRateAccommodationOffer } from './bundles'
import getAccommodationItemViewLoyaltyUpgradeAvailability from '../loyalty/getAccommodationItemViewLoyaltyUpgradeAvailability'
import { isPostPurchaseAncillaryCartMode } from 'checkout/selectors/payment/isPostPurchaseAncillaryPayment'
import { PAYMENT_SCHEDULE_STATUS } from 'constants/payment'
import { isHotelPackage, isLpcHotelOffer } from 'lib/offer/offerTypes'
import { getPropertyRateUpgradeSourceItemTotals, isPropertyRateLoyaltyPackageUpgradeItem } from 'checkout/lib/utils/loyalty/propertyRateLoyaltyUpgrade'
import type { IntlShape } from 'react-intl'
import { createLereSurchargePayloadForCheckoutItem, createLereSurchargePayloadForLoyaltyUpgradeTarget, getLereDynamicPricingSurchargeTokenForCheckoutItem, getLereDynamicPricingSurchargeTokenForStay } from '../lereDynamicPricing'
import { addDynamicPricingSurchargeToAppliedAccommodationLoyaltyUpgrade } from '../loyalty/accommodationLoyaltyUpgradeAvailability'
import { getPayableAccommodationSurcharge } from './priceView'
import getHotelDateChangePrice from './getHotelDateChangePrice'
import { isDerbysoftReservation } from 'lib/order/orderItemUtils'
import type { LereCacheContext } from './getLEPackageItemTotals'

type CheckoutAccommodationOffer = App.HotelOffer | App.TourOffer | App.VillaOffer

export function dummyItemTotalsView(): App.Checkout.ItemViewTotals {
  return {
    price: 0,
    memberPrice: 0,
    value: 0,
    surcharge: 0,
    taxesAndFees: 0,
    extraGuestSurcharge: 0,
    memberValue: 0,
  }
}

function dummyAccommodationItemView(
  intl: IntlShape,
  item: App.Checkout.LEAccommodationItem,
  kind: 'le',
  offer?: CheckoutAccommodationOffer,
): App.Checkout.LEAccommodationItemView {
  const dummy: App.Checkout.LEAccommodationItemView = {
    ...dummyItemTotalsView(),
    kind,
    packageId: getPackageId(item),
    uniqueKey: getItemUniqueKey(item),
    roomTypeId: '',
    item,
    primaryTitle: '',
    mainSummaryLabel: '',
    image: { id: '' },
    occupancy: { adults: 0, children: 0, infants: 0, teenagers: 0 },
    removeLabel: intl.formatMessage({
      id: '6GCTUDzlaHS2YZWTQ1KS',
      defaultMessage: 'Remove',
    }),
    partnership: { bonusPoints: 0, bonusDescription: '', localRewardConversionRate: 0 },
    totals: {
      extraGuestSurcharge: 0,
      price: 0,
      memberPrice: 0,
      taxesAndFees: 0,
      value: 0,
      memberValue: 0,
      surcharge: 0,
    },
    amenities: [],
    policies: '',
    hasTactical: false,
    inclusions: [],
    bonusInclusions: [],
    loyalty: {
      productType: getLoyaltyProductType(offer),
    },
    title: '',
  }

  return dummy
}

export function getLEPackageItemView(
  intl: IntlShape,
  item: App.Checkout.LEAccommodationItem,
  calendarsByOccupancy: App.CalendarState['calendarsByOccupancy'],
  availableRatesByOccupancy: { [offerId: string]: App.OfferAvailableRatesByOccupancy },
  propertyRatesByKey: App.OfferState['propertyRates'],
  checkoutWithMemberPrice: boolean,
  offer?: CheckoutAccommodationOffer,
  postPurchase?: App.CheckoutCartMode,
  existingOrder?: App.Order,
  luxPlusTier?: App.MembershipSubscriptionTier,
  canApplyLoyaltyRoomUpgrade?: boolean,
  adjustedPaymentSchedule?: App.AdjustedPaymentScheduleChargeData,
  lereSurchargeTokens?: App.OfferState['lereSurchargeTokens'],
  lereGroup?: string,
  lereCacheContext?: LereCacheContext,
): App.WithDataStatus<App.Checkout.LEAccommodationItemView> {
  if (!offer) {
    return {
      hasRequiredData: false,
      data: dummyAccommodationItemView(intl, item, 'le'),
    }
  }

  const uniqueKey = getItemUniqueKey(item)
  const originPackage = offer.packages.find(pkg => pkg.uniqueKey === uniqueKey)
  const isPropertyRateLoyaltyPackageUpgrade = isPropertyRateLoyaltyPackageUpgradeItem(item)
  let loyaltyUpgradeAvailability = canApplyLoyaltyRoomUpgrade && !isPropertyRateLoyaltyPackageUpgrade ? getAccommodationItemViewLoyaltyUpgradeAvailability(
    offer,
    item,
    calendarsByOccupancy,
    availableRatesByOccupancy,
    lereCacheContext,
  ) : undefined

  let itemTotals: App.Checkout.ItemViewTotals | undefined
  const propertyRateUpgradeSourceTotals = getPropertyRateUpgradeSourceItemTotals(item)
  if (propertyRateUpgradeSourceTotals) {
    itemTotals = propertyRateUpgradeSourceTotals
  } else if (offer && originPackage) {
    if (isInstantBookingLEHotelItem(item) && !isPostPurchaseAncillaryCartMode(postPurchase)) {
      itemTotals = getLEPackageItemTotals(
        offer,
        originPackage,
        item.checkIn,
        item.checkOut,
        item.occupancy,
        undefined,
        calendarsByOccupancy,
        availableRatesByOccupancy,
        {
          calendarsByOccupancyLereGroups: lereCacheContext?.calendarsByOccupancyLereGroups,
          offerAvailableRatesLereGroups: lereCacheContext?.offerAvailableRatesLereGroups,
          lereGroup,
        },
        lereCacheContext,
      )
    } else {
      // BNBL v2 charges the LE portion of the extra-guest surcharge upfront (svc-order folds it
      // into item.total, so it must sit in `surcharge` to keep the checkout charge reconciled),
      // while the payable-at-property portion goes to otherFees so it is disclosed as due at
      // property. `extraGuestSurcharge` carries the full amount for the per-item breakdown line.
      // A vendor-direct surcharge is excluded from the charge (see bnblUpfrontExtraGuestSurchargeCharge).
      const upfrontEgs = isBNBLLEHotelItem(item)
        ? bnblUpfrontExtraGuestSurchargeCharge(item.upfrontExtraGuestSurcharge, offer.surchargePaidDirectToVendor)
        : 0
      const upfrontEgsPayableAtProperty = isBNBLLEHotelItem(item) ? item.upfrontExtraGuestSurchargePayableAtProperty ?? 0 : 0
      itemTotals = {
        price: originPackage.price,
        memberPrice: originPackage.memberPrice,
        value: originPackage.value,
        taxesAndFees: originPackage.taxesAndFees,
        otherFees: {
          propertyFees: originPackage.propertyFees,
          extraGuestSurcharge: upfrontEgsPayableAtProperty,
        },
        memberValue: 0,
        surcharge: upfrontEgs,
        extraGuestSurcharge: upfrontEgs + upfrontEgsPayableAtProperty,
      }
    }
  } else if (existingOrder && isPostPurchaseAncillaryCartMode(postPurchase)) {
    const existingItem = existingOrder.items.find(orderItem => orderItem.transactionKey === item.transactionKey)
    if (existingItem) {
      itemTotals = {
        price: existingItem.packagePrice,
        memberPrice: existingItem.memberPrice,
        value: existingItem.package.value,
        taxesAndFees: 0,
        otherFees: {
          propertyFees: existingItem?.propertyFees,
        },
        memberValue: 0,
        surcharge: 0,
        extraGuestSurcharge: 0,
      }
    }
  }

  const hasAppliedLoyaltyUpgrade = !!(canApplyLoyaltyRoomUpgrade && item.loyaltyAppliedUpgradeType && !isPropertyRateLoyaltyPackageUpgrade)

  if (
    !itemTotals ||
    (hasAppliedLoyaltyUpgrade && !loyaltyUpgradeAvailability)
  ) {
    return {
      hasRequiredData: false,
      data: dummyAccommodationItemView(intl, item, 'le', offer),
    }
  }

  const targetPackageUniqueKey = item.loyaltyAppliedUpgradeType && !isPropertyRateLoyaltyPackageUpgrade ? originPackage?.loyalty.upgradeTargetsUniqueKeys?.[item.loyaltyAppliedUpgradeType] : undefined
  const pkg: App.Package | undefined = targetPackageUniqueKey
    ? offer.packages.find(pkg => pkg.uniqueKey === targetPackageUniqueKey)
    : originPackage

  // If the item has a new price, use that instead
  if (item.newPrice !== undefined) {
    itemTotals.price = item.newPrice
  }
  if (item.newSurcharge !== undefined) {
    itemTotals.surcharge = item.newSurcharge + (item.newExtraGuestSurcharge ?? itemTotals.extraGuestSurcharge ?? 0)
  }
  if (item.newExtraGuestSurcharge !== undefined) {
    itemTotals.extraGuestSurcharge = item.newExtraGuestSurcharge
  }

  const lereSurchargePayload = createLereSurchargePayloadForCheckoutItem(item, offer, pkg?.roomType?.id, lereGroup)
  const lereSurchargeToken = getLereDynamicPricingSurchargeTokenForCheckoutItem(
    item,
    offer,
    pkg?.roomType?.id,
    lereGroup,
    lereSurchargeTokens ?? {},
  )
  // The customer pays the source total. The order uses the target surcharge, with the
  // loyalty payment covering the difference between the complete target and source totals.
  const loyaltyTargetSurchargePayload = createLereSurchargePayloadForLoyaltyUpgradeTarget(
    item,
    offer,
    isHotelPackage(pkg) ? pkg : undefined,
    lereGroup,
    postPurchase,
  )
  const loyaltyTargetSurchargeToken = loyaltyTargetSurchargePayload ? getLereDynamicPricingSurchargeTokenForStay(
    loyaltyTargetSurchargePayload,
    lereSurchargeTokens ?? {},
  ) : undefined

  if (lereSurchargeToken?.status === 'success' && !itemTotals.dynamicPricingSurchargeIncludedInSource) {
    itemTotals.dynamicPricingSurcharge = lereSurchargeToken.surchargeAmount
  }
  if (loyaltyTargetSurchargeToken?.status === 'success') {
    loyaltyUpgradeAvailability = addDynamicPricingSurchargeToAppliedAccommodationLoyaltyUpgrade(
      loyaltyUpgradeAvailability,
      item.loyaltyAppliedUpgradeType,
      loyaltyTargetSurchargeToken.surchargeAmount,
    )
  }
  const payableSurcharge = getPayableAccommodationSurcharge(itemTotals)

  if (
    (postPurchase === 'change-package' ||
      postPurchase === 'select-date' ||
      postPurchase === 'add-extra-guests' ||
      postPurchase === 'change-dates'
    ) && 'orderItemId' in item && existingOrder) {
    const orderItem = existingOrder.items.find(orderItem => orderItem.id === item.orderItemId)
    if (orderItem) {
      itemTotals.paidPrice = orderItem.total

      if (existingOrder.paymentScheduleDetails && existingOrder.paymentScheduleDetails?.status === PAYMENT_SCHEDULE_STATUS.ACTIVE) {
        itemTotals.paymentScheduleAdjustment = adjustedPaymentSchedule?.summary.payNowAmount ?? 0
      }

      if (offer.type === 'hotel' && postPurchase === 'select-date') {
        // For BNBL select-date: preserve original package price, no reduction in surcharges if new package price is lower.
        const priceToUse = orderItem.packagePrice
        const memberPriceToUse = orderItem.packagePrice

        const nextPrice = checkoutWithMemberPrice && memberPriceToUse > 0 ? memberPriceToUse : priceToUse
        itemTotals.newPrice = nextPrice + ((itemTotals.isAvailable ? payableSurcharge : orderItem.reservation?.surcharge) || 0)

        // Calculate difference, but do not allow negative amounts
        const priceDifference = itemTotals.newPrice - orderItem.total
        itemTotals.price = Math.max(0, priceDifference)
        itemTotals.memberPrice = Math.max(0, priceDifference)
      } else if (
        (offer.type === 'hotel' && ['change-dates', 'add-extra-guests'].includes(postPurchase)) ||
        (isLpcHotelOffer(offer) && postPurchase === 'change-dates')
      ) {
        itemTotals.newPrice = getHotelDateChangePrice({
          orderItem,
          duration: item.duration,
          checkIn: postPurchase === 'change-dates' && 'checkIn' in item ? item.checkIn : undefined,
          checkOut: postPurchase === 'change-dates' && 'checkOut' in item ? item.checkOut : undefined,
          packageUniqueKey: pkg?.uniqueKey,
          isLpc: isLpcHotelOffer(offer),
          isDerbysoftDateChange: postPurchase === 'change-dates' && isDerbysoftReservation(orderItem),
          price: itemTotals.price,
          memberPrice: itemTotals.memberPrice,
          useMemberPrice: checkoutWithMemberPrice,
          surcharge: (itemTotals.isAvailable ? payableSurcharge : orderItem.reservation?.surcharge) || 0,
        })
        itemTotals.price = itemTotals.newPrice - orderItem.total
        itemTotals.memberPrice = itemTotals.newPrice - orderItem.total
      } else if (isLpcHotelOffer(offer) && postPurchase === 'add-extra-guests') {
        const nextPrice = checkoutWithMemberPrice && itemTotals.memberPrice > 0 ? itemTotals.memberPrice : itemTotals.price
        itemTotals.newPrice = nextPrice + payableSurcharge
        itemTotals.price = Math.max(0, itemTotals.newPrice - orderItem.total)
        itemTotals.memberPrice = Math.max(0, itemTotals.newPrice - orderItem.total)
      } else if (item.isUpdated) {
        const nextPrice = checkoutWithMemberPrice && itemTotals.memberPrice > 0 ? itemTotals.memberPrice : itemTotals.price
        itemTotals.newPrice = nextPrice + payableSurcharge
        itemTotals.price = itemTotals.newPrice - orderItem.total
        itemTotals.memberPrice = itemTotals.newPrice - orderItem.total
      } else {
        itemTotals.memberPrice = 0
        itemTotals.newPrice = 0
        itemTotals.price = 0
      }
      // clear those prices that we don't want to display
      itemTotals.value = 0
      itemTotals.surcharge = 0
      itemTotals.dynamicPricingSurcharge = 0
      itemTotals.taxesAndFees = 0
      itemTotals.extraGuestSurcharge = 0
      itemTotals.otherFees!.propertyFees = 0
    }
  }

  const checkIn = 'checkIn' in item ? moment(item.checkIn) : undefined
  const checkOut = 'checkOut' in item ? moment(item.checkOut) : undefined

  const [inclusions, bonusInclusions, luxPlusInclusions] = extractPkgInclusions(pkg, { checkIn, checkOut, luxPlusTier })
  const unifiedRate = isUnifiedRateAccommodationOffer(offer)
    ? findUnifiedRateForCheckoutItem(item, offer, propertyRatesByKey)
    : undefined
  // Only mandatory bundles are backed by a materialised bundle rate (found via propertyRateRef).
  // Opt-in parents are bundle-tagged but priced/described by the plain rate, so they take the
  // regular merge below and carry their ticket inclusions stamped on the item instead.
  const isBundleRateBacked = item.accommBundleIds?.length === 1 && !item.bundleOptin
  const mergedInclusions = isBundleRateBacked
    ? (unifiedRate?.inclusions ?? []).filter(
        i => (i.description ?? '').toLowerCase().trim() !== 'room only',
      )
    : uniqueBy([
        ...(inclusions ?? []),
        ...(unifiedRate?.inclusions ?? []),
      ], inclusion => `${inclusion.id ?? ''}::${inclusion.description ?? ''}`)

  const checkoutBundleInclusions = item.bundleOptin
    ? undefined
    : (unifiedRate?.bundleInclusions?.length ? unifiedRate.bundleInclusions : undefined)
  const checkoutOptInBundleInclusions = item.bundleOptin
    ? getOptInBundleInclusionsFromRate(unifiedRate, item)
    : undefined

  const data: App.Checkout.LEAccommodationItemView = {
    title: `${pkg?.roomType?.name}: ${offer.property?.name}`,
    totals: {
      ...itemTotals,
      isReservableForZero: pkg?.roomRate?.isReservableForZero,
    },
    kind: 'le',
    pkg,
    packageId: item.packageId,
    uniqueKey,
    roomTypeId: pkg?.roomType?.id ?? '',
    item,
    primaryTitle: offer.type === 'hotel' ? pkg?.name ?? '' : pkg?.roomType?.name ?? '',
    propertyRateName: unifiedRate?.name,
    mainSummaryLabel: pkg?.name ?? '',
    image: pkg?.roomType?.images[0] ?? offer.image,
    amenities: pkg?.roomType?.amenities ?? [],
    policies: pkg?.roomPolicyDescription ?? '',
    occupancy: item.occupancy,
    inclusions: mergedInclusions,
    bonusInclusions,
    bundleInclusions: checkoutBundleInclusions,
    optInBundleInclusions: checkoutOptInBundleInclusions,
    luxPlusInclusions,
    removeLabel: item.itemType === 'tourV1'
      ? intl.formatMessage({
          id: 'kMOB9efOHiyP5PebUXOJ',
          defaultMessage: 'Remove tour',
        })
      : intl.formatMessage(
          {
            id: '8q3c6d0Yp3bswGUzbmU1',
            defaultMessage: 'Remove {saleUnit}',
          },
          {
            saleUnit: offer?.saleUnitLong ?? intl.formatMessage({
              id: 'ExBPXgHuuX0D9_IQhNZG',
              defaultMessage: 'room',
            }),
          },
        ),
    partnership: pkg?.partnership,
    hasTactical: pkg?.hasTactical ?? false,
    loyalty: {
      productType: getLoyaltyProductType(offer),
    },
    mx: getHotelEncryptedMargin(offer?.type, pkg),
    offer,
    loyaltyUpgradeAvailability,
  }

  // Eligible LERE items wait for token success/failure; timeout failures continue unsurcharged.
  const lereSurchargeTokenReady = !lereSurchargePayload || lereSurchargeToken?.status === 'success' || lereSurchargeToken?.status === 'failed'
  const loyaltyTargetSurchargeTokenReady = !loyaltyTargetSurchargePayload || loyaltyTargetSurchargeToken?.status === 'success' || loyaltyTargetSurchargeToken?.status === 'failed'
  const hasRequiredData = (!!itemTotals.price || !!postPurchase) && lereSurchargeTokenReady && loyaltyTargetSurchargeTokenReady

  return {
    hasRequiredData,
    data,
  }
}
